import { WHATSAPP_NUMBER } from "@/lib/whatsapp";
import PolicyPage from "@/components/PolicyPage";

const Terms = () => (
  <PolicyPage
    testId="terms-page"
    overline="Legal"
    title="Terms of Service"
    updated="February 15, 2026"
    description="The terms that govern your use of Luxsova — orders, pricing, authenticity, warranties and dispute resolution."
  >
    <p>
      Welcome to Luxsova. By accessing or placing an order on <a href="https://luxsova.in">luxsova.in</a>,
      through our WhatsApp concierge, or via any of our official channels, you agree to be bound by
      these Terms of Service. Please read them carefully — they set out the ground rules between you
      and Luxsova (a brand operated by Apex Enterprises, India).
    </p>

    <h2>1. Who we are</h2>
    <p>
      Luxsova is a curated luxury marketplace. We sell handbags, fragrances, jewellery, apparel,
      footwear and accessories from established houses. Every piece is inspected, catalogued and
      dispatched from our facility with white-glove packaging.
    </p>

    <h2>2. Eligibility &amp; account</h2>
    <ul>
      <li>You must be 18 years or older, or have a parent / guardian&apos;s consent, to place an order.</li>
      <li>You are responsible for keeping your account credentials confidential and for any activity on your account.</li>
      <li>We may suspend or terminate any account we reasonably believe is being used for fraud, resale abuse, chargeback abuse or violation of these Terms.</li>
    </ul>

    <h2>3. Product listings &amp; authenticity</h2>
    <ul>
      <li>Every product is described to the best of our knowledge based on the source brand&apos;s data and our own quality inspection.</li>
      <li>Photographs are indicative — minor variation in colour, texture or hardware may occur due to lighting, screens or natural leather grain.</li>
      <li>All items are sold as authentic. If a piece is ever proven inauthentic by an independent authenticator recognised by the original brand, we will refund the full amount and cover the authentication cost — no questions asked.</li>
    </ul>

    <h2>4. Pricing, taxes &amp; currency</h2>
    <ul>
      <li>All prices are displayed in Indian Rupees (₹) and are inclusive of applicable GST unless stated otherwise.</li>
      <li>Prices can change at any time before an order is confirmed. Once an order is confirmed and paid, the confirmed price is locked in.</li>
      <li>If a product is mispriced due to a technical error and you have not yet been dispatched, we may cancel the order and refund you in full.</li>
    </ul>

    <h2>5. Placing an order</h2>
    <ol>
      <li>You select items and check out through the website or via WhatsApp with our concierge.</li>
      <li>An order is only <em>confirmed</em> once payment is verified — an order-received email or WhatsApp message is not, by itself, a confirmation of stock or price.</li>
      <li>Once confirmed, we will dispatch within the timelines outlined in our <a href="/shipping">Shipping &amp; Delivery Policy</a>.</li>
    </ol>

    <h2>6. Payments</h2>
    <p>
      We accept UPI, cards, net banking and select wallets through PCI-compliant payment gateways.
      We do not store your card details on our servers. Payments processed via WhatsApp use the same
      secure gateway links — never share card details, OTPs or CVVs in a chat message.
    </p>

    <h2>7. Cancellations, returns &amp; refunds</h2>
    <p>
      Cancellations and returns are governed by our <a href="/returns">Returns &amp; Refunds</a> policy.
      In summary: you may raise a return within 7 days of delivery. Original tags, dust bags and
      packaging must be intact. Fragrances, worn items and altered pieces are not eligible.
    </p>

    <h2>8. Intellectual property</h2>
    <p>
      All content on Luxsova — including the logo, editorial photography, product descriptions,
      code and UI — is the property of Apex Enterprises or its licensors. Brand names and marks
      referenced on product pages belong to their respective owners and are used solely to describe
      genuine articles for resale.
    </p>

    <h2>9. Prohibited use</h2>
    <ul>
      <li>Scraping, mirroring, or reselling our catalogue or images without written permission.</li>
      <li>Uploading reviews, images or content that infringe someone else&apos;s IP, are defamatory, obscene or unlawful.</li>
      <li>Using the site to launder funds, resell to prohibited jurisdictions, or evade taxes.</li>
    </ul>

    <h2>10. Limitation of liability</h2>
    <p>
      To the fullest extent permitted by law, Luxsova&apos;s aggregate liability arising out of or in
      connection with any order is limited to the amount you paid for the specific item(s) giving
      rise to the claim. We are not liable for indirect, incidental or consequential losses.
    </p>

    <h2>11. Governing law &amp; jurisdiction</h2>
    <p>
      These Terms are governed by the laws of India. Any dispute will be subject to the exclusive
      jurisdiction of the courts of Indore, Madhya Pradesh, unless mandatory consumer protection
      law provides otherwise.
    </p>

    <h2>12. Contact</h2>
    <p>
      Questions about these Terms? Reach us at <a href="mailto:care@luxsova.com">care@luxsova.com</a>{" "}
      or on <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer">WhatsApp</a>.
    </p>
  </PolicyPage>
);

export default Terms;
